import React, { useState, useEffect, useCallback } from 'react';
import { FaSort, FaFilter, FaTimes, FaIdCard, FaBus, FaRoute, FaClock, FaPhone, FaEnvelope, FaChartBar, FaEdit } from 'react-icons/fa';
import DriverAnalyticsModal from './DriverAnalyticsModal';
import { db, auth } from '../firebase';
import { collection, getDocs, query, where, orderBy, getDoc, doc, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

const DRIVER_LEVEL_OPTIONS = ['1', '2', '3', '4', '5'];
const DIVISION_OPTIONS = ['Tatu Campus', 'Athi Campus', 'Eldoret Campus'];

const Personnel = () => {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState(null);
  const [personnel, setPersonnel] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [rosters, setRosters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [expandedPersonnelId, setExpandedPersonnelId] = useState(null);
  const [loading] = useState(false);
  const [error, setError] = useState('');
  const [sortField, setSortField] = useState('firstName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    driverLevel: '',
    manager: '',
    division: ''
  });
  const [isFiltering, setIsFiltering] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [analyticsDriver, setAnalyticsDriver] = useState(null);
  const [managerOptions, setManagerOptions] = useState([]);
  const [selectedPersonnelIds, setSelectedPersonnelIds] = useState([]);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [bulkUpdateLoading, setBulkUpdateLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [bulkUpdateForm, setBulkUpdateForm] = useState({
    driverLevel: '',
    manager: '',
    division: ''
  });

  const checkAuthAndRole = useCallback(async () => {
    const user = auth.currentUser;
    
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        navigate('/dashboard');
        return;
      }
      
      const userData = userDoc.data();
      setUserRole(userData.role);
    } catch (err) {
      console.error('Error checking user role:', err);
      navigate('/login');
    }
  }, [navigate]);

  const fetchPersonnel = useCallback(async () => {
    try {
      setIsFiltering(true);
      setError('');
      
      // Fetch drivers and bus assistants
      const usersRef = collection(db, 'users');
      
      // Try to fetch with sorting, but fallback to no sorting if it fails
      let driversSnapshot, assistantsSnapshot;
      try {
        const driversQuery = query(
          usersRef,
          where('role', '==', 'driver'),
          orderBy(sortField, sortDirection)
        );
        const assistantsQuery = query(
          usersRef,
          where('role', '==', 'bus_assistant'),
          orderBy(sortField, sortDirection)
        );
        [driversSnapshot, assistantsSnapshot] = await Promise.all([
          getDocs(driversQuery),
          getDocs(assistantsQuery)
        ]);
      } catch (sortError) {
        // If sorting fails (e.g., missing index), fetch without sorting
        console.warn('Sorting failed, fetching without sort:', sortError);
        const driversQuery = query(usersRef, where('role', '==', 'driver'));
        const assistantsQuery = query(usersRef, where('role', '==', 'bus_assistant'));
        [driversSnapshot, assistantsSnapshot] = await Promise.all([
          getDocs(driversQuery),
          getDocs(assistantsQuery)
        ]);
      }

      let personnelList = [
        ...driversSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), role: 'driver' })),
        ...assistantsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), role: 'bus_assistant' }))
      ];

      // Apply client-side sorting if query sorting failed
      personnelList.sort((a, b) => {
        const aVal = a[sortField] || '';
        const bVal = b[sortField] || '';
        const comparison = aVal.toString().localeCompare(bVal.toString());
        return sortDirection === 'asc' ? comparison : -comparison;
      });

      // Apply search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        personnelList = personnelList.filter(person => 
          (person.firstName || '').toLowerCase().includes(searchLower) ||
          (person.lastName || '').toLowerCase().includes(searchLower) ||
          (person.email || '').toLowerCase().includes(searchLower) ||
          (person.phoneNumber || '').toLowerCase().includes(searchLower) ||
          (person.nationalId || '').toLowerCase().includes(searchLower) ||
          (person.driversLicense || '').toLowerCase().includes(searchLower) ||
          (person.driverLevel || '').toLowerCase().includes(searchLower) ||
          (person.manager || '').toLowerCase().includes(searchLower) ||
          (person.division || '').toLowerCase().includes(searchLower)
        );
      }

      // Apply role filter
      if (filters.role) {
        personnelList = personnelList.filter(person => person.role === filters.role);
      }

      if (filters.driverLevel) {
        personnelList = personnelList.filter(person => (person.driverLevel || '') === filters.driverLevel);
      }

      if (filters.manager) {
        personnelList = personnelList.filter(person => (person.manager || '') === filters.manager);
      }

      if (filters.division) {
        personnelList = personnelList.filter(person => (person.division || '') === filters.division);
      }

      setPersonnel(personnelList);
    } catch (err) {
      console.error('Error fetching personnel:', err);
      setError('Failed to fetch personnel. Please try again.');
    } finally {
      setIsFiltering(false);
    }
  }, [sortField, sortDirection, filters]);

  const fetchVehicles = useCallback(async () => {
    try {
      const vehiclesRef = collection(db, 'vehicles');
      const vehiclesSnapshot = await getDocs(vehiclesRef);
      const vehiclesList = vehiclesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setVehicles(vehiclesList);
    } catch (err) {
      console.error('Error fetching vehicles:', err);
    }
  }, []);

  const fetchRoutes = useCallback(async () => {
    try {
      const routesRef = collection(db, 'optimizedRoutes');
      const routesSnapshot = await getDocs(query(routesRef, where('status', '==', 'active')));
      const routesList = routesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRoutes(routesList);

      // Fetch rosters for routes
      const rosterIds = [...new Set(routesList.map(r => r.rosterId).filter(Boolean))];
      const rosterPromises = rosterIds.map(async (rosterId) => {
        try {
          const rosterDoc = await getDoc(doc(db, 'rosters', rosterId));
          if (rosterDoc.exists()) {
            return { id: rosterId, ...rosterDoc.data() };
          }
        } catch (error) {
          console.error('Error fetching roster:', error);
        }
        return null;
      });
      const rosterResults = await Promise.all(rosterPromises);
      const rostersMap = {};
      rosterResults.forEach(roster => {
        if (roster) {
          rostersMap[roster.id] = roster;
        }
      });
      setRosters(rostersMap);
    } catch (err) {
      console.error('Error fetching routes:', err);
    }
  }, []);

  const fetchManagers = useCallback(async () => {
    try {
      const usersRef = collection(db, 'users');
      let managerSnapshot;

      try {
        managerSnapshot = await getDocs(query(
          usersRef,
          where('role', '==', 'manager'),
          orderBy('firstName', 'asc')
        ));
      } catch (sortError) {
        console.warn('Manager sorting failed, fetching without sort:', sortError);
        managerSnapshot = await getDocs(query(usersRef, where('role', '==', 'manager')));
      }

      const managers = managerSnapshot.docs
        .map(managerDoc => {
          const data = managerDoc.data();
          const fullName = `${data.firstName || ''} ${data.lastName || ''}`.trim();
          return {
            id: managerDoc.id,
            label: fullName || data.email || 'Unnamed Manager'
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label));

      setManagerOptions(managers);
    } catch (err) {
      console.error('Error fetching managers:', err);
    }
  }, []);

  useEffect(() => {
    checkAuthAndRole();
  }, [checkAuthAndRole]);

  useEffect(() => {
    if (userRole) {
      fetchPersonnel();
      fetchVehicles();
      fetchRoutes();
      fetchManagers();
    }
  }, [userRole, fetchPersonnel, fetchVehicles, fetchRoutes, fetchManagers]);

  const getAssignedVehicle = (personnelId) => {
    return vehicles.find(vehicle => 
      (vehicle.assignedDriver && vehicle.assignedDriver.id === personnelId) ||
      (vehicle.assignedAssistant && vehicle.assignedAssistant.id === personnelId)
    );
  };

  const isCurrentlyOnTrip = (personnelId) => {
    const assignedVehicle = getAssignedVehicle(personnelId);
    if (!assignedVehicle) return null;

    // Find routes for this vehicle
    const vehicleRoutes = routes.filter(route => route.busId === assignedVehicle.id);
    
    for (const route of vehicleRoutes) {
      const roster = rosters[route.rosterId];
      if (isTripInSession(route, roster)) {
        return {
          route: route,
          vehicle: assignedVehicle
        };
      }
    }
    
    return null;
  };

  // Helper function to check if a trip is currently in session (from Track.js)
  const isTripInSession = (route, roster = null) => {
    if (!route || !route.time || route.time === 'N/A') {
      return false;
    }

    try {
      // Check roster date range if roster is provided
      if (roster) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Handle Firestore timestamp or ISO string
        let startDate = null;
        if (roster.date) {
          if (roster.date.toDate) {
            startDate = roster.date.toDate();
          } else {
            startDate = new Date(roster.date);
          }
          startDate.setHours(0, 0, 0, 0);
        }

        let endDate = null;
        if (roster.endDate) {
          if (roster.endDate.toDate) {
            endDate = roster.endDate.toDate();
          } else {
            endDate = new Date(roster.endDate);
          }
          endDate.setHours(23, 59, 59, 999);
        }

        // Check if today is within the roster date range
        if (startDate && today < startDate) {
          return false; // Trip hasn't started yet
        }

        if (endDate && today > endDate) {
          return false; // Trip has ended
        }

        // Check frequency-based day matching
        const frequency = roster.frequency || route.rosterFrequency || 'daily';
        const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

        if (frequency === 'once') {
          if (startDate && today.getTime() !== startDate.getTime()) {
            return false;
          }
        } else if (frequency === 'weekdays') {
          if (dayOfWeek === 0 || dayOfWeek === 6) {
            return false; // It's a weekend
          }

          if (roster.weekdays && Array.isArray(roster.weekdays) && roster.weekdays.length > 0) {
            // Helper function to convert weekday names to numbers (1=Monday, 7=Sunday)
            const convertWeekdayNameToNumber = (weekdayName) => {
              const weekdayMap = {
                'Monday': 1,
                'Tuesday': 2,
                'Wednesday': 3,
                'Thursday': 4,
                'Friday': 5,
                'Saturday': 6,
                'Sunday': 7
              };
              return weekdayMap[weekdayName] || null;
            };
            
            // Convert weekday names to numbers if they are strings
            const rosterWeekdaysNumbers = roster.weekdays.map(day => {
              if (typeof day === 'string') {
                return convertWeekdayNameToNumber(day);
              }
              return day; // Already a number
            }).filter(day => day !== null);
            
            const rosterDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;
            if (!rosterWeekdaysNumbers.includes(rosterDayOfWeek)) {
              return false;
            }
          }
        }
      }

      // Parse the route time
      const timeParts = route.time.split(':');
      if (timeParts.length < 2) {
        return false;
      }

      const routeHours = parseInt(timeParts[0], 10);
      const routeMinutes = parseInt(timeParts[1], 10);

      if (isNaN(routeHours) || isNaN(routeMinutes)) {
        return false;
      }

      // Get current date and time
      const now = new Date();
      const currentHours = now.getHours();
      const currentMinutes = now.getMinutes();
      const currentTimeInMinutes = currentHours * 60 + currentMinutes;

      // Calculate route start time in minutes
      const routeStartTimeInMinutes = routeHours * 60 + routeMinutes;

      // Get total duration with wait time (in seconds)
      const totalDurationSeconds = route.routeMetrics?.totalDurationWithWait || 0;
      if (totalDurationSeconds === 0) {
        return false;
      }

      // Calculate route end time in minutes
      const routeEndTimeInMinutes = routeStartTimeInMinutes + Math.ceil(totalDurationSeconds / 60);

      // Check if current time falls within the trip window
      const bufferMinutes = 5;
      const adjustedStartTime = routeStartTimeInMinutes - bufferMinutes;

      if (currentTimeInMinutes >= adjustedStartTime && currentTimeInMinutes <= routeEndTimeInMinutes) {
        return true;
      }

      // Check if we're in the next day's window (for trips that span midnight)
      if (routeEndTimeInMinutes > 24 * 60) {
        const nextDayEndTime = routeEndTimeInMinutes - (24 * 60);
        if (currentTimeInMinutes <= nextDayEndTime) {
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('Error checking if trip is in session:', error);
      return false;
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setTimeout(() => {
      fetchPersonnel();
    }, 300);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      role: '',
      driverLevel: '',
      manager: '',
      division: ''
    });
  };

  const handleExpandPersonnel = (person) => {
    if (expandedPersonnelId === person.id) {
      setExpandedPersonnelId(null);
    } else {
      setExpandedPersonnelId(person.id);
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(personnel.length / pageSize);
  const paginatedPersonnel = personnel.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const selectedPersonnelCount = selectedPersonnelIds.length;
  const allVisibleSelected = paginatedPersonnel.length > 0 && paginatedPersonnel.every(person => selectedPersonnelIds.includes(person.id));
  const someVisibleSelected = paginatedPersonnel.some(person => selectedPersonnelIds.includes(person.id));

  useEffect(() => {
    const visiblePersonnelIds = new Set(personnel.map(person => person.id));
    setSelectedPersonnelIds(prev => prev.filter(id => visiblePersonnelIds.has(id)));
  }, [personnel]);

  const handleTogglePersonnelSelection = (personnelId) => {
    setSelectedPersonnelIds(prev =>
      prev.includes(personnelId)
        ? prev.filter(id => id !== personnelId)
        : [...prev, personnelId]
    );
  };

  const handleToggleVisibleSelection = () => {
    const visibleIds = paginatedPersonnel.map(person => person.id);
    if (visibleIds.length === 0) {
      return;
    }

    setSelectedPersonnelIds(prev => {
      if (visibleIds.every(id => prev.includes(id))) {
        return prev.filter(id => !visibleIds.includes(id));
      }

      return [...new Set([...prev, ...visibleIds])];
    });
  };

  const handleBulkUpdateInputChange = (e) => {
    const { name, value } = e.target;
    setBulkUpdateForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetBulkUpdateForm = () => {
    setBulkUpdateForm({
      driverLevel: '',
      manager: '',
      division: ''
    });
  };

  const closeBulkUpdateModal = () => {
    if (bulkUpdateLoading) {
      return;
    }

    setShowBulkUpdateModal(false);
    resetBulkUpdateForm();
  };

  const handleBulkUpdateSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (selectedPersonnelIds.length === 0) {
      setError('Select at least one person to bulk update.');
      return;
    }

    const trimmedDriverLevel = bulkUpdateForm.driverLevel.trim();
    const trimmedManager = bulkUpdateForm.manager.trim();
    const trimmedDivision = bulkUpdateForm.division.trim();

    if (!trimmedDriverLevel || !trimmedManager || !trimmedDivision) {
      setError('Driver level, manager, and division are all required.');
      return;
    }

    setBulkUpdateLoading(true);

    try {
      const updatePayload = {
        driverLevel: trimmedDriverLevel,
        manager: trimmedManager,
        division: trimmedDivision,
        updatedAt: new Date().toISOString()
      };

      await Promise.all(
        selectedPersonnelIds.map(personnelId =>
          updateDoc(doc(db, 'users', personnelId), updatePayload)
        )
      );

      setSelectedPersonnelIds([]);
      setShowBulkUpdateModal(false);
      resetBulkUpdateForm();
      setSuccess(`Updated ${selectedPersonnelIds.length} personnel profile${selectedPersonnelIds.length === 1 ? '' : 's'}.`);
      await fetchPersonnel();
    } catch (err) {
      console.error('Error bulk updating personnel:', err);
      setError(err.message || 'Failed to bulk update personnel.');
    } finally {
      setBulkUpdateLoading(false);
    }
  };

  if (!userRole) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00E0E0] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-700">Loading Personnel...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-black">Personnel Management</h1>
          <div className="flex space-x-4">
            <button
              onClick={() => {
                setError('');
                setSuccess('');
                setShowBulkUpdateModal(true);
              }}
              disabled={selectedPersonnelCount === 0}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-[#00E0E0] text-[#008B8B] font-semibold hover:bg-[#00E0E0]/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            >
              <FaEdit />
              <span>Bulk Update</span>
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
            >
              <FaFilter />
              <span>Filters</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-500/10 border border-green-500 text-green-700 px-4 py-3 rounded-lg mb-6">
            {success}
          </div>
        )}

        {/* Filters */}
        {showFilters && (
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <input
                  type="text"
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  placeholder="Search personnel..."
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <select
                  name="role"
                  value={filters.role}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                >
                  <option value="">All Roles</option>
                  <option value="driver">Driver</option>
                  <option value="bus_assistant">Bus Assistant</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Driver Level</label>
                <select
                  name="driverLevel"
                  value={filters.driverLevel}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                >
                  <option value="">All Levels</option>
                  {DRIVER_LEVEL_OPTIONS.map(level => (
                    <option key={level} value={level}>
                      Level {level}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Manager</label>
                <select
                  name="manager"
                  value={filters.manager}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                >
                  <option value="">All Managers</option>
                  {managerOptions.map(manager => (
                    <option key={manager.id} value={manager.label}>
                      {manager.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Division</label>
                <select
                  name="division"
                  value={filters.division}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                >
                  <option value="">All Divisions</option>
                  {DIVISION_OPTIONS.map(division => (
                    <option key={division} value={division}>
                      {division}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-1 md:col-span-5 flex justify-end items-center space-x-4">
                {isFiltering && (
                  <div className="text-sm text-gray-500">
                    <span className="animate-pulse">Filtering...</span>
                  </div>
                )}
                <button
                  onClick={clearFilters}
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                >
                  <FaTimes />
                  <span>Clear Filters</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Personnel List Controls */}
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-gray-700 font-medium">
            Showing {paginatedPersonnel.length} of {personnel.length} personnel
            {selectedPersonnelCount > 0 && (
              <span className="ml-3 text-[#008B8B]">{selectedPersonnelCount} selected</span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <label htmlFor="pageSize" className="text-sm text-gray-700">Rows per page:</label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
              className="px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00E0E0] bg-white"
              style={{ minWidth: '80px' }}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {/* Personnel List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={handleToggleVisibleSelection}
                    aria-label={someVisibleSelected && !allVisibleSelected ? 'Select visible personnel' : 'Select all visible personnel'}
                    className="h-4 w-4 rounded border-gray-300 text-[#00B8B8] focus:ring-[#00E0E0]"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('firstName')}>
                  <div className="flex items-center space-x-1">
                    <span>Name</span>
                    <FaSort className={`text-gray-400 ${sortField === 'firstName' ? 'text-gray-600' : ''}`} />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('role')}>
                  <div className="flex items-center space-x-1">
                    <span>Role</span>
                    <FaSort className={`text-gray-400 ${sortField === 'role' ? 'text-gray-600' : ''}`} />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <span>Assigned Bus</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('driverLevel')}>
                  <div className="flex items-center space-x-1">
                    <span>Driver Level</span>
                    <FaSort className={`text-gray-400 ${sortField === 'driverLevel' ? 'text-gray-600' : ''}`} />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('manager')}>
                  <div className="flex items-center space-x-1">
                    <span>Manager</span>
                    <FaSort className={`text-gray-400 ${sortField === 'manager' ? 'text-gray-600' : ''}`} />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('division')}>
                  <div className="flex items-center space-x-1">
                    <span>Division</span>
                    <FaSort className={`text-gray-400 ${sortField === 'division' ? 'text-gray-600' : ''}`} />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <span>Actions</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedPersonnel.map((person) => {
                const assignedVehicle = getAssignedVehicle(person.id);
                const currentTrip = isCurrentlyOnTrip(person.id);
                
                return (
                  <React.Fragment key={person.id}>
                    <tr className="cursor-pointer hover:bg-gray-50" onClick={() => handleExpandPersonnel(person)}>
                      <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedPersonnelIds.includes(person.id)}
                          onChange={() => handleTogglePersonnelSelection(person.id)}
                          aria-label={`Select ${(person.firstName || '').trim()} ${(person.lastName || '').trim()}`.trim()}
                          className="h-4 w-4 rounded border-gray-300 text-[#00B8B8] focus:ring-[#00E0E0]"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {person.firstName} {person.lastName}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          person.role === 'driver' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {person.role === 'driver' ? 'Driver' : 'Bus Assistant'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {assignedVehicle ? (
                          <div className="flex items-center space-x-2">
                            <FaBus className="text-gray-400" />
                            <span>{assignedVehicle.registrationNumber || assignedVehicle.fleetNumber || 'N/A'}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic">Not assigned</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {person.driverLevel ? `Level ${person.driverLevel}` : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {person.manager || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {person.division || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {currentTrip ? (
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 flex items-center space-x-1">
                            <FaRoute />
                            <span>On Trip</span>
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
                            Available
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/users?edit=${person.id}`); }}
                            className="p-2 rounded-lg text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 transition-colors"
                            title="Edit User"
                          >
                            <FaEdit />
                          </button>
                          {person.role === 'driver' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setAnalyticsDriver(person); }}
className="p-2 rounded-lg text-blue-600 hover:text-blue-900 hover:bg-blue-50 transition-colors"
                            title="Driver Behaviour Analytics"
                            >
                              <FaChartBar />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {/* Dropdown for details */}
                    {expandedPersonnelId === person.id && (
                      <tr>
                        <td colSpan={9} className="bg-gray-50">
                          <div className="px-6 py-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                              {/* Left Column - Personal Details */}
                              <div>
                                <div className="font-semibold mb-4 text-[#00B8B8]">Personal Information</div>
                                <div className="space-y-3">
                                  <div className="flex items-center space-x-3">
                                    <FaIdCard className="text-gray-400" />
                                    <div>
                                      <div className="text-xs text-gray-500">National ID</div>
                                      <div className="text-sm font-medium text-gray-900">{person.nationalId || 'N/A'}</div>
                                    </div>
                                  </div>
                                  {person.role === 'driver' && (
                                    <div className="flex items-center space-x-3">
                                      <FaIdCard className="text-gray-400" />
                                      <div>
                                        <div className="text-xs text-gray-500">Driver's License</div>
                                        <div className="text-sm font-medium text-gray-900">{person.driversLicense || 'N/A'}</div>
                                      </div>
                                    </div>
                                  )}
                                  <div className="flex items-center space-x-3">
                                    <FaPhone className="text-gray-400" />
                                    <div>
                                      <div className="text-xs text-gray-500">Phone Number</div>
                                      <div className="text-sm font-medium text-gray-900">{person.phoneNumber || 'N/A'}</div>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-3">
                                    <FaEnvelope className="text-gray-400" />
                                    <div>
                                      <div className="text-xs text-gray-500">Email</div>
                                      <div className="text-sm font-medium text-gray-900">{person.email || 'N/A'}</div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div>
                                <div className="font-semibold mb-4 text-[#00B8B8]">Profile Details</div>
                                <div className="space-y-3">
                                  <div className="flex items-center space-x-3">
                                    <FaIdCard className="text-gray-400" />
                                    <div>
                                      <div className="text-xs text-gray-500">Driver Level</div>
                                      <div className="text-sm font-medium text-gray-900">{person.driverLevel || 'N/A'}</div>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-3">
                                    <FaIdCard className="text-gray-400" />
                                    <div>
                                      <div className="text-xs text-gray-500">Manager</div>
                                      <div className="text-sm font-medium text-gray-900">{person.manager || 'N/A'}</div>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-3">
                                    <FaIdCard className="text-gray-400" />
                                    <div>
                                      <div className="text-xs text-gray-500">Division</div>
                                      <div className="text-sm font-medium text-gray-900">{person.division || 'N/A'}</div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Right Column - Assignment & Trip Info */}
                              <div>
                                <div className="font-semibold mb-4 text-[#00B8B8]">Assignment & Status</div>
                                <div className="space-y-3">
                                  <div className="flex items-center space-x-3">
                                    <FaBus className="text-gray-400" />
                                    <div>
                                      <div className="text-xs text-gray-500">Assigned Bus</div>
                                      <div className="text-sm font-medium text-gray-900">
                                        {assignedVehicle ? (
                                          <span>{assignedVehicle.registrationNumber || assignedVehicle.fleetNumber || 'N/A'}</span>
                                        ) : (
                                          <span className="text-gray-400 italic">Not assigned</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {currentTrip && (
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                      <div className="flex items-center space-x-2 mb-2">
                                        <FaRoute className="text-green-600" />
                                        <div className="font-semibold text-green-800">Currently On Trip</div>
                                      </div>
                                      <div className="space-y-2 text-sm">
                                        <div>
                                          <span className="text-gray-600">Route: </span>
                                          <span className="font-medium text-gray-900">{currentTrip.route.name || 'N/A'}</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          <FaClock className="text-gray-400" />
                                          <span className="text-gray-600">Time: </span>
                                          <span className="font-medium text-gray-900">{currentTrip.route.time || 'N/A'}</span>
                                        </div>
                                        <div>
                                          <span className="text-gray-600">Trip Type: </span>
                                          <span className="font-medium text-gray-900">{currentTrip.route.tripType || 'N/A'}</span>
                                        </div>
                                        {currentTrip.route.rosterName && (
                                          <div>
                                            <span className="text-gray-600">Roster: </span>
                                            <span className="font-medium text-gray-900">{currentTrip.route.rosterName}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {!currentTrip && assignedVehicle && (
                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                      <div className="text-sm text-gray-600">
                                        No active trip scheduled at this time
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="flex justify-end items-center mt-4 space-x-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">Page {currentPage} of {totalPages}</span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {analyticsDriver && (
        <DriverAnalyticsModal
          driver={analyticsDriver}
          onClose={() => setAnalyticsDriver(null)}
        />
      )}

      {showBulkUpdateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Bulk Update Personnel</h2>
                <p className="mt-1 text-sm text-gray-500">
                  Update {selectedPersonnelCount} selected profile{selectedPersonnelCount === 1 ? '' : 's'}.
                </p>
              </div>
              <button
                onClick={closeBulkUpdateModal}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                aria-label="Close bulk update modal"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleBulkUpdateSubmit} className="space-y-4 px-6 py-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Driver Level</label>
                <select
                  name="driverLevel"
                  value={bulkUpdateForm.driverLevel}
                  onChange={handleBulkUpdateInputChange}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                >
                  <option value="">Select driver level</option>
                  {DRIVER_LEVEL_OPTIONS.map(level => (
                    <option key={level} value={level}>
                      Level {level}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Manager</label>
                <select
                  name="manager"
                  value={bulkUpdateForm.manager}
                  onChange={handleBulkUpdateInputChange}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                >
                  <option value="">Select manager</option>
                  {managerOptions.map(manager => (
                    <option key={manager.id} value={manager.label}>
                      {manager.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Division</label>
                <select
                  name="division"
                  value={bulkUpdateForm.division}
                  onChange={handleBulkUpdateInputChange}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                >
                  <option value="">Select division</option>
                  {DIVISION_OPTIONS.map(division => (
                    <option key={division} value={division}>
                      {division}
                    </option>
                  ))}
                </select>
              </div>

              <div className="rounded-lg bg-gray-50 px-4 py-3 text-sm text-gray-600">
                This updates only driver level, manager, division, and the user profile timestamp.
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={closeBulkUpdateModal}
                  disabled={bulkUpdateLoading}
                  className="rounded-lg border border-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={bulkUpdateLoading || selectedPersonnelCount === 0}
                  className="rounded-lg bg-[#00B8B8] px-4 py-2 font-semibold text-white hover:bg-[#009999] transition-colors disabled:opacity-50"
                >
                  {bulkUpdateLoading ? 'Updating...' : 'Save Bulk Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Personnel;
